import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { FaDownload, FaShare, FaArrowLeft, FaSpinner } from 'react-icons/fa';
import api from '../utils/api';

const CertificateView = () => {
    const { courseId, studentId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const certificateRef = useRef();
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [certificateData, setCertificateData] = useState({
        studentName: location.state?.studentName || '',
        courseTitle: location.state?.courseTitle || '',
        instructorName: '',
        issueDate: new Date().toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        }),
        certificateId: `CERT-${Math.random().toString(36).substr(2, 9).toUpperCase()}`
    });

    useEffect(() => {
        const fetchCertificateData = async () => {
            try {
                // Fetch course details
                const { data: course } = await api.get(`/courses/${courseId}`);
                
                // If we don't have student name from state, fetch user data
                let studentName = location.state?.studentName;
                if (!studentName) {
                    const { data: user } = await api.get(`/users/${studentId}`);
                    studentName = user.name;
                }

                setCertificateData({
                    studentName: studentName || 'Student',
                    courseTitle: course.title,
                    instructorName: typeof course.instructor === 'object' ? course.instructor.name : course.instructor,
                    issueDate: new Date().toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                    }),
                    certificateId: `CERT-${courseId.substring(0, 8).toUpperCase()}-${studentId.substring(0, 8).toUpperCase()}`
                });
            } catch (err) {
                console.error('Error fetching certificate data:', err);
                setError('Failed to load certificate data');
            } finally {
                setLoading(false);
            }
        };

        fetchCertificateData();
    }, [courseId, studentId, location.state]);

    const handleDownload = () => {
        // const certificate = certificateRef.current;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Set canvas size (A4 proportions - landscape)
        canvas.width = 1200;
        canvas.height = 850;
        
        // Background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Decorative border
        ctx.strokeStyle = '#0d6efd';
        ctx.lineWidth = 10;
        ctx.strokeRect(20, 20, canvas.width - 40, canvas.height - 40);
        
        ctx.strokeStyle = '#17a2b8';
        ctx.lineWidth = 2;
        ctx.strokeRect(40, 40, canvas.width - 80, canvas.height - 80);
        
        // Header decoration
        ctx.fillStyle = '#0d6efd';
        ctx.font = 'bold 80px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('🎓', canvas.width / 2, 140);
        
        // Title
        ctx.fillStyle = '#0d6efd';
        ctx.font = 'bold 56px Georgia';
        ctx.fillText('Certificate of Completion', canvas.width / 2, 230);
        
        // Decorative line
        ctx.strokeStyle = '#17a2b8';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(300, 250);
        ctx.lineTo(900, 250);
        ctx.stroke();
        
        // Subtitle
        ctx.fillStyle = '#555555';
        ctx.font = 'italic 24px Georgia';
        ctx.fillText('This is to certify that', canvas.width / 2, 310);
        
        // Student Name
        ctx.fillStyle = '#000000';
        ctx.font = 'bold 48px Georgia';
        ctx.fillText(certificateData.studentName, canvas.width / 2, 385);
        
        // Underline student name
        const nameWidth = ctx.measureText(certificateData.studentName).width;
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo((canvas.width - nameWidth) / 2, 395);
        ctx.lineTo((canvas.width + nameWidth) / 2, 395);
        ctx.stroke();
        
        // Completion text
        ctx.fillStyle = '#555555';
        ctx.font = '26px Georgia';
        ctx.fillText('has successfully completed the course', canvas.width / 2, 450);
        
        // Course Title
        ctx.fillStyle = '#0d6efd';
        ctx.font = 'bold 36px Georgia';
        const maxWidth = canvas.width - 200;
        const courseTitle = certificateData.courseTitle;
        if (ctx.measureText(courseTitle).width > maxWidth) {
            ctx.font = 'bold 30px Georgia';
        }
        ctx.fillText(courseTitle, canvas.width / 2, 515);
        
        // Issue Date
        ctx.fillStyle = '#666666';
        ctx.font = '22px Georgia';
        ctx.fillText(`Issued on ${certificateData.issueDate}`, canvas.width / 2, 575);
        
        // Bottom section - signatures
        // Instructor section
        ctx.fillStyle = '#333333';
        ctx.font = '18px Arial';
        ctx.textAlign = 'left';
        ctx.fillText('Course Instructor', 150, 700);
        
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(150, 720);
        ctx.lineTo(450, 720);
        ctx.stroke();
        
        ctx.fillStyle = '#000000';
        ctx.font = 'bold 22px Arial';
        ctx.fillText(certificateData.instructorName, 150, 745);
        
        // Platform section
        ctx.fillStyle = '#333333';
        ctx.font = '18px Arial';
        ctx.textAlign = 'right';
        ctx.fillText('Fundisha Learning Platform', canvas.width - 150, 700);
        
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(canvas.width - 450, 720);
        ctx.lineTo(canvas.width - 150, 720);
        ctx.stroke();
        
        ctx.fillStyle = '#666666';
        ctx.font = '18px Arial';
        ctx.fillText(`Certificate ID: ${certificateData.certificateId}`, canvas.width - 150, 745);
        
        // Watermark
        ctx.fillStyle = 'rgba(13, 110, 253, 0.05)';
        ctx.font = 'bold 120px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('FUNDISHA', canvas.width / 2, canvas.height / 2);
        
        // Convert to blob and download
        canvas.toBlob((blob) => {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.download = `Certificate_${certificateData.studentName.replace(/\s+/g, '_')}_${certificateData.courseTitle.replace(/\s+/g, '_')}.png`;
            link.href = url;
            link.click();
            URL.revokeObjectURL(url);
        });
    };

    const handleShare = () => {
        const shareUrl = window.location.href;
        const shareText = `I've completed ${certificateData.courseTitle} on Fundisha! 🎓`;
        
        if (navigator.share) {
            navigator.share({
                title: 'Certificate of Completion',
                text: shareText,
                url: shareUrl
            }).catch(err => console.log('Error sharing:', err));
        } else {
            // Fallback - copy to clipboard
            navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
            alert('Certificate link copied to clipboard!');
        }
    };

    if (loading) {
        return (
            <div className="container py-5 text-center" style={{ marginTop: '75px', minHeight: '70vh' }}>
                <FaSpinner className="fa-spin fa-3x text-primary mb-3" />
                <h4>Loading certificate...</h4>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container py-5 text-center" style={{ marginTop: '75px', minHeight: '70vh' }}>
                <h4 className="text-danger">{error}</h4>
                <button className="btn btn-primary mt-3" onClick={() => navigate(-1)}>
                    Go Back
                </button>
            </div>
        );
    }

    return (
        <div className="container py-5" style={{ marginTop: '75px', minHeight: '70vh' }}>
            <div className="row mb-4">
                <div className="col-12">
                    <button className="btn btn-outline-primary" onClick={() => navigate(-1)}>
                        <FaArrowLeft className="me-2" />
                        Back
                    </button>
                </div>
            </div>

            <div className="row justify-content-center">
                <div className="col-lg-10">
                    {/* Action Buttons */}
                    <div className="d-flex justify-content-end gap-2 mb-3">
                        <button className="btn btn-primary" onClick={handleDownload}>
                            <FaDownload className="me-2" />
                            Download Certificate
                        </button>
                        <button className="btn btn-success" onClick={handleShare}>
                            <FaShare className="me-2" />
                            Share
                        </button>
                    </div>

                    {/* Certificate Display */}
                    <div 
                        ref={certificateRef}
                        className="certificate-container bg-white p-5 shadow-lg"
                        style={{
                            border: '10px solid #0d6efd',
                            borderRadius: '10px',
                            position: 'relative',
                            minHeight: '600px'
                        }}
                    >
                        {/* Inner Border */}
                        <div style={{
                            border: '2px solid #17a2b8',
                            padding: '40px',
                            height: '100%',
                            position: 'relative'
                        }}>
                            {/* Watermark */}
                            <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                fontSize: '120px',
                                fontWeight: 'bold',
                                color: 'rgba(13, 110, 253, 0.05)',
                                userSelect: 'none',
                                zIndex: 0
                            }}>
                                FUNDISHA
                            </div>

                            {/* Content */}
                            <div style={{ position: 'relative', zIndex: 1 }}>
                                {/* Icon */}
                                <div className="text-center mb-4">
                                    <span style={{ fontSize: '80px' }}>🎓</span>
                                </div>

                                {/* Title */}
                                <h1 className="text-center mb-3" style={{ 
                                    color: '#0d6efd', 
                                    fontFamily: 'Georgia, serif',
                                    fontSize: '3rem',
                                    fontWeight: 'bold'
                                }}>
                                    Certificate of Completion
                                </h1>

                                <hr style={{ 
                                    width: '50%', 
                                    margin: '0 auto 30px', 
                                    borderTop: '3px solid #17a2b8' 
                                }} />

                                {/* Subtitle */}
                                <p className="text-center mb-4" style={{ 
                                    fontStyle: 'italic', 
                                    fontSize: '1.3rem',
                                    color: '#555'
                                }}>
                                    This is to certify that
                                </p>

                                {/* Student Name */}
                                <h2 className="text-center mb-2" style={{ 
                                    fontFamily: 'Georgia, serif',
                                    fontSize: '2.5rem',
                                    fontWeight: 'bold'
                                }}>
                                    {certificateData.studentName}
                                </h2>
                                <hr style={{ 
                                    width: '40%', 
                                    margin: '0 auto 30px', 
                                    borderTop: '2px solid #000' 
                                }} />

                                {/* Completion Text */}
                                <p className="text-center mb-3" style={{ 
                                    fontSize: '1.3rem',
                                    color: '#555'
                                }}>
                                    has successfully completed the course
                                </p>

                                {/* Course Title */}
                                <h3 className="text-center mb-4" style={{ 
                                    color: '#0d6efd',
                                    fontFamily: 'Georgia, serif',
                                    fontSize: '2rem',
                                    fontWeight: 'bold'
                                }}>
                                    {certificateData.courseTitle}
                                </h3>

                                {/* Issue Date */}
                                <p className="text-center mb-5" style={{ 
                                    fontSize: '1.1rem',
                                    color: '#666'
                                }}>
                                    Issued on {certificateData.issueDate}
                                </p>

                                {/* Signatures */}
                                <div className="row mt-5 pt-4">
                                    <div className="col-md-6 text-center">
                                        <div className="mb-2" style={{ 
                                            borderTop: '2px solid #000',
                                            width: '60%',
                                            margin: '0 auto'
                                        }}></div>
                                        <p className="mb-0" style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                                            {certificateData.instructorName}
                                        </p>
                                        <p style={{ color: '#666' }}>Course Instructor</p>
                                    </div>
                                    <div className="col-md-6 text-center">
                                        <div className="mb-2" style={{ 
                                            borderTop: '2px solid #000',
                                            width: '60%',
                                            margin: '0 auto'
                                        }}></div>
                                        <p className="mb-0" style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                                            Fundisha Platform
                                        </p>
                                        <p style={{ color: '#666', fontSize: '0.9rem' }}>
                                            ID: {certificateData.certificateId}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Print Instructions */}
                    <div className="alert alert-info mt-4">
                        <strong>Tip:</strong> Use the Download button to save a high-quality image of your certificate, 
                        or use your browser's print function (Ctrl+P / Cmd+P) to print directly.
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CertificateView;
